import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { bookingService } from '../../services/bookingService';
import './BookingModal.css';

const BookingModal = ({
  facility,
  user,
  bookingData,
  setBookingData,
  bookingStep,
  setBookingStep,
  onClose,
  onSubmit,
  equipmentByCategory: propsEquipmentByCategory,
  toggleEquipment,
  updateEquipmentQuantity,
  removeEquipment,
  loading,
  // Props for dependent approval
  requiresApproval = false,
  approvalFile = null,
  setApprovalFile = () => {},
  approvalFileName = '',
  setApprovalFileName = () => {},
  // Props for notifications
  addNotification = () => {}
}) => {
  // Add availability checking states
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availabilityStatus, setAvailabilityStatus] = useState(null);
  const [conflictingBookings, setConflictingBookings] = useState([]);
  
  // Equipment data state - fetched from SQL via PUBLIC endpoint
  const [equipmentByCategory, setEquipmentByCategory] = useState(propsEquipmentByCategory || {});
  const [loadingEquipment, setLoadingEquipment] = useState(false);
  const [equipmentError, setEquipmentError] = useState(null);

  // PUBLIC function to fetch equipment from SQL (NO AUTH REQUIRED)
  const fetchPublicEquipment = async () => {
    const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? '/api'
      : 'https://nlngraclub.com/nlnapi';
    
    setLoadingEquipment(true);
    setEquipmentError(null);
    
    try {
      console.log('📡 BookingModal: Fetching equipment from SQL via PUBLIC endpoint...');
      
      // PUBLIC endpoint - NO Authorization header needed!
      const response = await fetch(`${API_BASE_URL}/equipment.php?action=list&_t=${Date.now()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('📦 Equipment API Response:', data);
      
      if (data.success && data.equipment) {
        const equipment = data.equipment;
        
        // Group equipment by category
        const grouped = equipment.reduce((acc, item) => {
          const category = item.category || 'Uncategorized';
          if (!acc[category]) {
            acc[category] = [];
          }
          acc[category].push(item);
          return acc;
        }, {});
        
        setEquipmentByCategory(grouped);
        console.log(`✅ Loaded ${equipment.length} equipment items from SQL for modal`);
        console.log(`📊 Categories: ${Object.keys(grouped).join(', ')}`);
        
        // Show success message for equipment load
        if (addNotification) {
          addNotification(`✅ Equipment catalog loaded (${equipment.length} items available)`, 'success');
        }
      } else {
        console.warn('⚠️ No equipment data received or API returned error:', data);
        setEquipmentError(data.error || 'No equipment data received');
        setEquipmentByCategory({});
        
        if (addNotification) {
          addNotification('⚠️ Could not load equipment catalog. Please refresh the page.', 'warning');
        }
      }
    } catch (error) {
      console.error('❌ Error fetching equipment for modal:', error);
      setEquipmentError(error.message);
      setEquipmentByCategory({});
      
      if (addNotification) {
        addNotification(`❌ Failed to load equipment: ${error.message}`, 'error');
      }
    } finally {
      setLoadingEquipment(false);
    }
  };

  // Fetch equipment from SQL if not provided as prop
  useEffect(() => {
    if (!propsEquipmentByCategory || Object.keys(propsEquipmentByCategory).length === 0) {
      fetchPublicEquipment();
    } else {
      setEquipmentByCategory(propsEquipmentByCategory);
    }
  }, [propsEquipmentByCategory]);

  // Check availability when date or time changes
  useEffect(() => {
    const checkAvailability = async () => {
      if (bookingStep === 1 && bookingData.date && bookingData.startTime && bookingData.endTime) {
        setCheckingAvailability(true);
        setAvailabilityStatus(null);
        
        try {
          const response = await bookingService.checkAvailability({
            facility_id: facility.id,
            booking_date: bookingData.date,
            start_time: bookingData.startTime,
            end_time: bookingData.endTime
          });
          
          if (response.success) {
            if (response.available) {
              setAvailabilityStatus('available');
              // Don't show notification for availability - it would be too noisy
            } else {
              setAvailabilityStatus('unavailable');
              setConflictingBookings(response.conflicting_bookings || []);
              
              // Show warning for unavailable slot
              if (addNotification) {
                addNotification(`⚠️ This time slot is already booked. Please select another time.`, 'warning');
              }
            }
          } else {
            setAvailabilityStatus('error');
            if (addNotification) {
              addNotification(`❌ Could not check availability: ${response.message}`, 'error');
            }
          }
        } catch (error) {
          console.error('Availability check error:', error);
          setAvailabilityStatus('error');
          if (addNotification) {
            addNotification(`❌ Failed to check availability: ${error.message}`, 'error');
          }
        } finally {
          setCheckingAvailability(false);
        }
      }
    };
    
    const timer = setTimeout(checkAvailability, 800);
    return () => clearTimeout(timer);
  }, [bookingData.date, bookingData.startTime, bookingData.endTime, facility.id, bookingStep, addNotification]);

  // Helper function to get emoji for category
  const getCategoryEmoji = (category) => {
    const emojiMap = {
      'Kitchen': '🍳',
      'Furniture': '🪑',
      'Electronics': '📺',
      'Sports': '⚽',
      'Sporting Equipment': '⚽',
      'Audio': '🎵',
      'Lighting': '💡',
      'Seating': '🪑',
      'Tables': '📐',
      'Linens': '🧵',
      'Visual': '📺',
      'Stage': '🎭',
      'Decor': '🎨',
      'Serving': '🍽️',
      'AV Equipment': '📹',
      'Office Equipment': '📎',
      'Safety': '🛡️',
      'Wellness': '🧘',
      'Aquatics': '🏊',
      'Dance': '💃',
      'Martial Arts': '🥋',
      'Mind Sports': '♟️',
      'Games': '🎮',
      'Social': '👥'
    };
    return emojiMap[category] || '🔧';
  };

  // Handle file upload for dependent approval
  const handleFileUpload = (file) => {
    if (file) {
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      const maxSize = 5 * 1024 * 1024; // 5MB
      
      if (!allowedTypes.includes(file.type)) {
        if (addNotification) {
          addNotification('❌ Invalid file type. Please upload PDF, JPG, or PNG.', 'error');
        }
        return;
      }
      
      if (file.size > maxSize) {
        if (addNotification) {
          addNotification('❌ File too large. Maximum size is 5MB.', 'error');
        }
        return;
      }
      
      setApprovalFile(file);
      setApprovalFileName(file.name);
      if (addNotification) {
        addNotification(`✅ Approval document "${file.name}" uploaded successfully`, 'success');
      }
    }
  };

  // Handle file removal
  const handleFileRemove = () => {
    setApprovalFile(null);
    setApprovalFileName('');
    if (addNotification) {
      addNotification('📄 Approval document removed', 'info');
    }
  };

  return (
    <motion.div 
      className="modal-overlay-3d"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div 
        className="modal-container-3d"
        initial={{ scale: 0.9, y: 50 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 50 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        onClick={e => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="modal-header-3d">
          <div className="header-content">
            <div className="header-icon-3d">
              <span className="header-emoji">📅</span>
            </div>
            <div className="header-text">
              <h2>Request Booking</h2>
              <span className="facility-name">{facility.name}</span>
            </div>
          </div>
          <motion.button 
            className="close-btn-3d"
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            onClick={onClose}
            disabled={loading}
          >
            <span className="close-icon">✕</span>
          </motion.button>
        </div>

        <div className="modal-body-3d">
          {/* Facility Info Card */}
          <motion.div 
            className="facility-card-3d"
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <div className="facility-icon-3d">
              <span className="facility-emoji">🏛️</span>
            </div>
            <div className="facility-details">
              <h3>{facility.name}</h3>
              <p>{facility.description?.substring(0, 100)}...</p>
              <div className="facility-badges">
                {facility.sportingOnly && (
                  <span className="badge-3d sporting">
                    <span className="badge-icon">⚽</span>
                    Sporting Only
                  </span>
                )}
                {facility.requiresApproval && (
                  <span className="badge-3d approval">
                    <span className="badge-icon">⏳</span>
                    Requires Approval
                  </span>
                )}
              </div>
            </div>
          </motion.div>

          {/* Dependent Warning Banner */}
          {requiresApproval && bookingStep === 4 && (
            <motion.div 
              className="dependent-warning-banner"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <i className="fa-solid fa-file-upload"></i>
              <div className="warning-content">
                <strong>Dependent Member Notice:</strong>
                <p>Please upload an approval document from your primary member to complete this booking.</p>
              </div>
            </motion.div>
          )}

          {/* 3D Progress Steps */}
          <div className="steps-container-3d">
            <div className="steps-track">
              {[1, 2, 3, 4].map((step) => (
                <React.Fragment key={step}>
                  <div className="step-wrapper">
                    <motion.div 
                      className={`step-indicator-3d ${bookingStep >= step ? 'active' : ''}`}
                      animate={bookingStep >= step ? { scale: [1, 1.1, 1] } : {}}
                      transition={{ duration: 0.3 }}
                    >
                      <span className="step-number">{step}</span>
                    </motion.div>
                    <span className={`step-label ${bookingStep >= step ? 'active' : ''}`}>
                      {step === 1 && 'Date & Time'}
                      {step === 2 && 'Details'}
                      {step === 3 && 'Equipment'}
                      {step === 4 && 'Confirm'}
                    </span>
                  </div>
                  {step < 4 && (
                    <div className={`step-connector ${bookingStep > step ? 'active' : ''}`}>
                      <div className="connector-line"></div>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Step Content */}
          <div className="step-content-3d">
            <AnimatePresence mode="wait">
              <motion.div
                key={bookingStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                {/* Step 1: Date & Time */}
                {bookingStep === 1 && (
                  <div className="step-panel">
                    <div className="panel-title">
                      <span className="title-emoji">📅</span>
                      <h3>Select Date & Time</h3>
                    </div>
                    
                    <div className="form-group-3d">
                      <label className="label-3d">
                        <span className="label-emoji">📆</span>
                        Choose Date
                      </label>
                      <div className="input-wrapper-3d">
                        <input 
                          type="date" 
                          className="input-3d"
                          min={new Date().toISOString().split('T')[0]}
                          value={bookingData.date}
                          onChange={(e) => setBookingData({...bookingData, date: e.target.value})}
                          disabled={loading}
                        />
                      </div>
                    </div>

                    <div className="time-group">
                      <div className="form-group-3d">
                        <label className="label-3d">
                          <span className="label-emoji">⏰</span>
                          Start Time
                        </label>
                        <div className="input-wrapper-3d">
                          <select
                            className="select-3d"
                            value={bookingData.startTime}
                            onChange={(e) => setBookingData({...bookingData, startTime: e.target.value})}
                            disabled={loading}
                          >
                            <option value="">Select start time</option>
                            {['8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', 
                              '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM', 
                              '6:00 PM', '7:00 PM', '8:00 PM'].map(time => (
                              <option key={time} value={time}>{time}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="form-group-3d">
                        <label className="label-3d">
                          <span className="label-emoji">⏱️</span>
                          End Time
                        </label>
                        <div className="input-wrapper-3d">
                          <select
                            className="select-3d"
                            value={bookingData.endTime}
                            onChange={(e) => setBookingData({...bookingData, endTime: e.target.value})}
                            disabled={loading}
                          >
                            <option value="">Select end time</option>
                            {['9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', 
                              '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM', 
                              '7:00 PM', '8:00 PM', '9:00 PM', '10:00 PM'].map(time => (
                              <option key={time} value={time}>{time}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* AVAILABILITY STATUS DISPLAY */}
                    {bookingData.date && bookingData.startTime && bookingData.endTime && (
                      <motion.div 
                        className={`availability-status-3d ${availabilityStatus}`}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        {checkingAvailability ? (
                          <div className="status-checking">
                            <div className="spinner-small"></div>
                            Checking availability...
                          </div>
                        ) : availabilityStatus === 'available' ? (
                          <div className="status-available">
                            <span className="status-icon">✓</span>
                            <span>This time slot is available! ✅</span>
                          </div>
                        ) : availabilityStatus === 'unavailable' ? (
                          <div className="status-unavailable">
                            <span className="status-icon">✕</span>
                            <div>
                              <strong>This time slot is already booked ❌</strong>
                              {conflictingBookings.length > 0 && (
                                <div className="conflicting-details">
                                  <p>Conflicting booking(s):</p>
                                  <ul>
                                    {conflictingBookings.map(b => (
                                      <li key={b.id}>
                                        {b.start_time} to {b.end_time}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </div>
                        ) : availabilityStatus === 'error' ? (
                          <div className="status-error">
                            <span className="status-icon">⚠️</span>
                            <span>Unable to check availability. Please try again.</span>
                          </div>
                        ) : null}
                      </motion.div>
                    )}
                  </div>
                )}

                {/* Step 2: Details */}
                {bookingStep === 2 && (
                  <div className="step-panel">
                    <div className="panel-title">
                      <span className="title-emoji">📋</span>
                      <h3>Event Details</h3>
                    </div>

                    <div className="form-group-3d">
                      <label className="label-3d">
                        <span className="label-emoji">🏷️</span>
                        Event Type *
                      </label>
                      <div className="input-wrapper-3d">
                        <select
                          className="select-3d"
                          value={bookingData.eventType}
                          onChange={(e) => setBookingData({...bookingData, eventType: e.target.value})}
                          disabled={loading}
                        >
                          {facility.sportingOnly ? (
                            <option value="sporting">Sporting Activity</option>
                          ) : (
                            <>
                              <option value="private">Private Function</option>
                              <option value="corporate">Corporate/Company</option>
                              <option value="club">Club Section</option>
                              <option value="religious">Religious Event*</option>
                              <option value="political">Political Event*</option>
                              <option value="social">Social Gathering</option>
                            </>
                          )}
                        </select>
                      </div>
                      {(bookingData.eventType === 'religious' || bookingData.eventType === 'political') && (
                        <motion.div 
                          className="warning-card-3d"
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                        >
                          <span className="warning-icon">⚠️</span>
                          <p>Requires pre-clearance from CSS, ESD or Manager</p>
                        </motion.div>
                      )}
                    </div>

                    {(bookingData.eventType === 'religious' || bookingData.eventType === 'political') && (
                      <div className="form-group-3d">
                        <label className="label-3d">
                          <span className="label-emoji">📎</span>
                          Upload Clearance Document *
                        </label>
                        <div className="file-input-3d">
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                setBookingData({...bookingData, clearanceFile: file});
                                if (addNotification) {
                                  addNotification(`✅ Clearance document "${file.name}" uploaded`, 'success');
                                }
                              }
                            }}
                            disabled={loading}
                          />
                          <span className="file-placeholder">Choose file...</span>
                        </div>
                      </div>
                    )}

                    <div className="form-row">
                      <div className="form-group-3d half">
                        <label className="label-3d">
                          <span className="label-emoji">👥</span>
                          Number of Guests
                        </label>
                        <div className="input-wrapper-3d">
                          <input 
                            type="number" 
                            className="input-3d"
                            min="1" 
                            max="500"
                            value={bookingData.guests}
                            onChange={(e) => setBookingData({...bookingData, guests: parseInt(e.target.value)})}
                            disabled={loading}
                          />
                        </div>
                      </div>

                      <div className="form-group-3d half">
                        <label className="label-3d">
                          <span className="label-emoji">📞</span>
                          Phone Number *
                        </label>
                        <div className="input-wrapper-3d">
                          <input
                            type="tel"
                            className="input-3d"
                            placeholder="08012345678"
                            value={bookingData.phone || user?.phone || ''}
                            onChange={(e) => setBookingData({...bookingData, phone: e.target.value})}
                            disabled={loading}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="form-group-3d">
                      <label className="label-3d">
                        <span className="label-emoji">💬</span>
                        WhatsApp Number (Optional)
                      </label>
                      <div className="input-wrapper-3d">
                        <input
                          type="tel"
                          className="input-3d"
                          placeholder="08012345678"
                          value={bookingData.whatsapp || user?.whatsApp || ''}
                          onChange={(e) => setBookingData({...bookingData, whatsapp: e.target.value})}
                          disabled={loading}
                        />
                      </div>
                    </div>

                    {bookingData.residentialArea && (
                      <motion.div 
                        className="residential-card-3d"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                      >
                        <div className="residential-icon">🏠</div>
                        <div className="residential-content">
                          <strong>This event is in a residential area.</strong>
                          <p>You must inform neighbors and minimize noise after 9pm.</p>
                          <label className="checkbox-3d">
                            <input 
                              type="checkbox"
                              checked={bookingData.noiseAfter9pm}
                              onChange={(e) => setBookingData({...bookingData, noiseAfter9pm: e.target.checked})}
                              disabled={loading}
                            />
                            <span className="checkbox-check"></span>
                            <span>I will inform neighbors and minimize noise</span>
                          </label>
                        </div>
                      </motion.div>
                    )}

                    <div className="priority-card-3d">
                      <span className="priority-icon">ℹ️</span>
                      <p>
                        <strong>Note:</strong> Club and Company functions have priority. 
                        Request will be sent to the Club President for approval.
                      </p>
                    </div>

                    <div className="form-group-3d">
                      <label className="label-3d">
                        <span className="label-emoji">📝</span>
                        Special Requests / Notes
                      </label>
                      <div className="textarea-wrapper-3d">
                        <textarea
                          className="textarea-3d"
                          rows="3"
                          placeholder="Any special requirements?"
                          value={bookingData.notes}
                          onChange={(e) => setBookingData({...bookingData, notes: e.target.value})}
                          disabled={loading}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 3: Equipment */}
                {bookingStep === 3 && (
                  <div className="step-panel">
                    <div className="panel-title">
                      <span className="title-emoji">🔧</span>
                      <h3>Select Equipment</h3>
                      <span className="optional-badge">Optional</span>
                    </div>

                    {loadingEquipment ? (
                      <div className="equipment-loading">
                        <div className="spinner-small"></div>
                        <p>Loading equipment from database...</p>
                      </div>
                    ) : equipmentError ? (
                      <div className="equipment-error">
                        <span className="error-icon">⚠️</span>
                        <p>{equipmentError}</p>
                        <button 
                          className="retry-btn"
                          onClick={fetchPublicEquipment}
                        >
                          🔄 Retry
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="equipment-header">
                          <div className="selected-count-3d">
                            <span className="count-badge">{bookingData.equipment.length}</span>
                            <span>items selected</span>
                          </div>
                          {bookingData.equipment.length > 0 && (
                            <motion.button 
                              className="clear-btn-3d"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => {
                                setBookingData({...bookingData, equipment: []});
                                if (addNotification) {
                                  addNotification('🗑️ All equipment cleared', 'info');
                                }
                              }}
                              disabled={loading}
                            >
                              <span className="clear-icon">✕</span>
                              Clear All
                            </motion.button>
                          )}
                        </div>

                        <AnimatePresence>
                          {bookingData.equipment.length > 0 && (
                            <motion.div 
                              className="selected-items-3d"
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                            >
                              <h4>Selected Items</h4>
                              {bookingData.equipment.map(item => (
                                <motion.div 
                                  key={item.id}
                                  className="selected-item-3d"
                                  layout
                                  initial={{ opacity: 0, x: -20 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  exit={{ opacity: 0, x: 20 }}
                                >
                                  <div className="item-info">
                                    <span className="item-emoji">{item.emoji || getCategoryEmoji(item.category) || '🔧'}</span>
                                    <span className="item-name">{item.name}</span>
                                  </div>
                                  <div className="quantity-controls">
                                    <motion.button 
                                      className="quantity-btn"
                                      whileHover={{ scale: 1.1 }}
                                      whileTap={{ scale: 0.9 }}
                                      onClick={() => updateEquipmentQuantity(item.id, item.quantity - 1)}
                                      disabled={loading || item.quantity <= 1}
                                    >
                                      −
                                    </motion.button>
                                    <span className="quantity">{item.quantity}</span>
                                    <motion.button 
                                      className="quantity-btn"
                                      whileHover={{ scale: 1.1 }}
                                      whileTap={{ scale: 0.9 }}
                                      onClick={() => updateEquipmentQuantity(item.id, item.quantity + 1)}
                                      disabled={loading || item.quantity >= item.available}
                                    >
                                      +
                                    </motion.button>
                                  </div>
                                  <motion.button 
                                    className="remove-item-btn"
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => {
                                      removeEquipment(item.id);
                                      if (addNotification) {
                                        addNotification(`🗑️ Removed ${item.name}`, 'info');
                                      }
                                    }}
                                    disabled={loading}
                                  >
                                    ✕
                                  </motion.button>
                                </motion.div>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>

                        <div className="equipment-categories-3d">
                          {Object.entries(equipmentByCategory).map(([category, items]) => {
                            const filteredItems = facility.sportingOnly 
                              ? items.filter(item => item.sporting_only === 1 || item.sportingOnly === true)
                              : items;
                            
                            if (filteredItems.length === 0) return null;
                            
                            return (
                              <div key={category} className="category-section">
                                <h4 className="category-title">
                                  <span className="category-emoji">{getCategoryEmoji(category)}</span>
                                  {category}
                                </h4>
                                <div className="equipment-grid-3d">
                                  {filteredItems.map(item => {
                                    const isSelected = bookingData.equipment.some(e => e.id === item.id);
                                    return (
                                      <motion.button
                                        key={item.id}
                                        className={`equipment-card-3d ${isSelected ? 'selected' : ''}`}
                                        onClick={() => {
                                          toggleEquipment(item);
                                          if (!isSelected && addNotification) {
                                            addNotification(`✅ Added ${item.name} to your request`, 'success');
                                          }
                                        }}
                                        whileHover={{ y: -4, scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        disabled={loading}
                                      >
                                        <div className="card-icon">
                                          <span className="item-emoji">{getCategoryEmoji(category)}</span>
                                        </div>
                                        <span className="item-name">{item.name}</span>
                                        <span className="item-available">{item.available || 0} left</span>
                                        {isSelected && (
                                          <motion.div 
                                            className="selected-check"
                                            initial={{ scale: 0 }}
                                            animate={{ scale: 1 }}
                                          >
                                            ✓
                                          </motion.div>
                                        )}
                                      </motion.button>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Step 4: Confirm */}
                {bookingStep === 4 && (
                  <div className="step-panel">
                    <div className="panel-title">
                      <span className="title-emoji">✓</span>
                      <h3>Review & Confirm</h3>
                    </div>

                    <motion.div 
                      className="summary-card-3d"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                    >
                      <div className="summary-header">
                        <div className="summary-id">
                          <span className="id-label">Status</span>
                          <span className="id-value">Pending Approval</span>
                        </div>
                        <div className="status-badge-3d pending">Awaiting Review</div>
                      </div>

                      <div className="summary-grid">
                        <div className="summary-row">
                          <span className="summary-label">Requestor</span>
                          <span className="summary-value">{user?.first_name} {user?.last_name}</span>
                        </div>
                        <div className="summary-row">
                          <span className="summary-label">Member Type</span>
                          <span className="summary-value">
                            {user?.membership_type === 'primary' ? 'Primary Member' : 
                             user?.relationship_type === 'spouse' ? 'Spouse' : 'Dependent Member'}
                          </span>
                        </div>
                        <div className="summary-row">
                          <span className="summary-label">Facility</span>
                          <span className="summary-value">{facility.name}</span>
                        </div>
                        <div className="summary-row">
                          <span className="summary-label">Location</span>
                          <span className="summary-value">{facility.location}</span>
                        </div>
                        <div className="summary-row">
                          <span className="summary-label">Date</span>
                          <span className="summary-value">{new Date(bookingData.date).toLocaleDateString()}</span>
                        </div>
                        <div className="summary-row">
                          <span className="summary-label">Time</span>
                          <span className="summary-value">{bookingData.startTime} - {bookingData.endTime}</span>
                        </div>
                        <div className="summary-row">
                          <span className="summary-label">Event Type</span>
                          <span className="summary-value">
                            <span className="event-badge">{bookingData.eventType}</span>
                          </span>
                        </div>
                        <div className="summary-row">
                          <span className="summary-label">Guests</span>
                          <span className="summary-value">{bookingData.guests}</span>
                        </div>
                        
                        {bookingData.equipment.length > 0 && (
                          <div className="summary-row equipment-summary">
                            <span className="summary-label">Equipment</span>
                            <div className="equipment-tags">
                              {bookingData.equipment.map(e => (
                                <span key={e.id} className="equipment-tag">
                                  {getCategoryEmoji(e.category) || '🔧'} {e.name} x{e.quantity}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {bookingData.notes && (
                          <div className="summary-row notes-summary">
                            <span className="summary-label">Notes</span>
                            <span className="summary-value notes">{bookingData.notes}</span>
                          </div>
                        )}
                        
                        {(bookingData.phone || user?.phone) && (
                          <div className="summary-row">
                            <span className="summary-label">Phone</span>
                            <span className="summary-value">{bookingData.phone || user?.phone}</span>
                          </div>
                        )}
                        {(bookingData.whatsapp || user?.whatsApp) && (
                          <div className="summary-row">
                            <span className="summary-label">WhatsApp</span>
                            <span className="summary-value">{bookingData.whatsapp || user?.whatsApp}</span>
                          </div>
                        )}
                      </div>
                    </motion.div>

                    {/* Dependent Approval File Upload Section */}
                    {requiresApproval && (
                      <motion.div 
                        className="dependent-approval-section"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                      >
                        <h3 className="section-title">
                          <span className="title-icon">📄</span>
                          Dependent Approval Document <span className="required-star">*</span>
                        </h3>
                        <div className="approval-upload-area">
                          <div className="upload-instructions">
                            <i className="fa-solid fa-info-circle"></i>
                            <p>As a dependent member, you need to upload an approval document from your primary member (parent/sponsor) to complete this booking.</p>
                          </div>
                          
                          <div className="file-upload-container">
                            <label className={`file-upload-label ${approvalFileName ? 'has-file' : ''}`}>
                              <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    handleFileUpload(file);
                                  }
                                }}
                                style={{ display: 'none' }}
                              />
                              <div className="upload-button">
                                <i className="fa-solid fa-cloud-upload-alt"></i>
                                <span>{approvalFileName || 'Click to upload approval document'}</span>
                              </div>
                            </label>
                            <p className="upload-hint">
                              <i className="fa-regular fa-circle-check"></i>
                              Accepted formats: PDF, JPG, PNG (Max 5MB)
                            </p>
                          </div>
                          
                          {approvalFile && (
                            <div className="uploaded-file-info">
                              <i className="fa-solid fa-check-circle"></i>
                              <span className="file-name">{approvalFileName}</span>
                              <button 
                                type="button"
                                className="remove-file-btn"
                                onClick={handleFileRemove}
                              >
                                <i className="fa-solid fa-times"></i>
                              </button>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}

                    <div className="terms-section">
                      <label className="checkbox-3d large">
                        <input type="checkbox" id="terms" disabled={loading} />
                        <span className="checkbox-check"></span>
                        <span className="terms-text">
                          I confirm that I am a club member and agree to the Terms of Use
                        </span>
                      </label>
                    </div>

                    <motion.div 
                      className="approval-notice-3d"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.3 }}
                    >
                      <span className="notice-icon">ℹ️</span>
                      <p>
                        <strong>Request will be sent to the Club President for approval.</strong>
                        {requiresApproval && ' Your approval document will be reviewed along with your booking request.'}
                      </p>
                    </motion.div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Modal Footer */}
          <div className="modal-footer-3d">
            {bookingStep > 1 && (
              <motion.button 
                className="btn-3d secondary"
                whileHover={{ x: -4 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => setBookingStep(bookingStep - 1)}
                disabled={loading}
              >
                <span className="btn-icon">←</span>
                Back
              </motion.button>
            )}
            {bookingStep < 4 ? (
              <motion.button 
                className="btn-3d primary"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setBookingStep(bookingStep + 1)}
                disabled={
                  loading ||
                  (bookingStep === 1 && (
                    !bookingData.date || 
                    !bookingData.startTime || 
                    !bookingData.endTime ||
                    availabilityStatus !== 'available'
                  )) ||
                  (bookingStep === 2 && !bookingData.guests) ||
                  (bookingStep === 2 && !bookingData.phone && !user?.phone) ||
                  (bookingStep === 2 && (bookingData.eventType === 'religious' || bookingData.eventType === 'political') && !bookingData.clearanceFile) ||
                  (bookingStep === 2 && bookingData.eventType === 'private' && bookingData.residentialArea && !bookingData.commitmentForm)
                }
              >
                Continue
                <span className="btn-icon">→</span>
              </motion.button>
            ) : (
              <motion.button 
                className={`btn-3d primary submit ${requiresApproval && !approvalFile ? 'disabled' : ''}`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onSubmit}
                disabled={loading || (requiresApproval && !approvalFile)}
              >
                <span className="btn-icon">{loading ? '⏳' : '📤'}</span>
                {loading ? 'Submitting...' : (requiresApproval ? 'Submit with Approval Document' : 'Submit Request')}
              </motion.button>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default BookingModal;